import { ApolloClient, gql } from 'apollo-boost';

export interface CIAccount {
  accountId: number;
  accountName: string;
  accountType?: string;
  userId: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  kind: string;
  ticks: string;
  password?: string | null;
  createdBy: string | null;
}

export interface CITestPair {
  ticks: string;
  createdBy: string | null;
  donor: CIAccount | null;
  npo: CIAccount | null;
}

export interface CITestAccountsResponse {
  ciTestAccounts: {
    total: number;
    page: number;
    pageSize: number;
    items: CITestPair[];
  };
}

const ciTestAccountsQuery = gql`
  query CITestAccounts($page: Int, $pageSize: Int) {
    ciTestAccounts(page: $page, pageSize: $pageSize) {
      total
      page
      pageSize
      items {
        ticks
        createdBy
        donor {
          accountId
          accountName
          accountType
          userId
          email
          firstName
          lastName
          kind
          ticks
          createdBy
        }
        npo {
          accountId
          accountName
          accountType
          userId
          email
          firstName
          lastName
          kind
          ticks
          createdBy
        }
      }
    }
  }
`;

export const getCITestAccounts = (
  client: ApolloClient<object>,
  variables: { page: number; pageSize: number },
) =>
  client.query<CITestAccountsResponse>({
    query: ciTestAccountsQuery,
    variables,
    fetchPolicy: 'network-only',
  });

const ciCreateTestAccountsMutation = gql`
  mutation CICreateTestAccounts(
    $email: String!
    $firstName: String
    $lastName: String
  ) {
    ciCreateTestAccounts(
      email: $email
      firstName: $firstName
      lastName: $lastName
    ) {
      success
      ticks
      password
      portfolioId
      programId
      investmentId
      invitationId
      donor {
        accountId
        accountName
        accountType
        userId
        email
        firstName
        lastName
        kind
        ticks
        password
        createdBy
      }
      npo {
        accountId
        accountName
        accountType
        userId
        email
        firstName
        lastName
        kind
        ticks
        password
        createdBy
      }
    }
  }
`;

export interface CICreateTestAccountsResponse {
  ciCreateTestAccounts: {
    success: boolean;
    ticks: string;
    password: string;
    portfolioId: number | null;
    programId: number | null;
    investmentId: number | null;
    invitationId: number | null;
    donor: CIAccount;
    npo: CIAccount;
  };
}

export const ciCreateTestAccounts = (
  client: ApolloClient<object>,
  variables: { email: string; firstName?: string; lastName?: string },
) =>
  client.mutate<CICreateTestAccountsResponse>({
    mutation: ciCreateTestAccountsMutation,
    variables,
  });

const ciCleanupTestAccountMutation = gql`
  mutation CICleanupTestAccount($email: String!) {
    ciCleanupTestAccount(email: $email) {
      success
      summary {
        accounts
        users
        portfolios
        programs
        investments
        invitations
        allocations
        reports
      }
    }
  }
`;

export interface CICleanupSummary {
  accounts: number;
  users: number;
  portfolios: number;
  programs: number;
  investments: number;
  invitations: number;
  allocations: number;
  reports: number;
}

export interface CICleanupTestAccountResponse {
  ciCleanupTestAccount: {
    success: boolean;
    summary: CICleanupSummary;
  };
}

export const ciCleanupTestAccount = (
  client: ApolloClient<object>,
  email: string,
) =>
  client.mutate<CICleanupTestAccountResponse>({
    mutation: ciCleanupTestAccountMutation,
    variables: { email },
  });

const ciAddInvestmentToDonorMutation = gql`
  mutation CiAddInvestmentToDonor(
    $donorAccountId: Int!
    $npoEmail: String!
    $amount: Decimal!
  ) {
    ciAddInvestmentToDonor(
      donorAccountId: $donorAccountId
      npoEmail: $npoEmail
      amount: $amount
    ) {
      success
      donorAccountId
      granteeAccountId
      portfolioId
      investmentId
      invitationId
    }
  }
`;

export interface CIAddInvestmentToDonorResponse {
  ciAddInvestmentToDonor: {
    success: boolean;
    donorAccountId: number;
    granteeAccountId: number;
    portfolioId: number | null;
    investmentId: number | null;
    invitationId: number | null;
  };
}

export const ciAddInvestmentToDonor = (
  client: ApolloClient<object>,
  variables: { donorAccountId: number; npoEmail: string; amount: number },
) =>
  client.mutate<CIAddInvestmentToDonorResponse>({
    mutation: ciAddInvestmentToDonorMutation,
    variables,
  });
